
import * as auth from './auth'
import sleep from './sleep'
import log from './log'

/**
 * Log context.
 */

const ctx = log.fields({ scope: 'actions' })

/**
 * Actions class.
 */

export default class Actions {

  /**
   * Initialize.
   */

  constructor({ state = {}, client, render, error }) {
    this.client = client
    this.render = render
    this.state = state
    this.error = err => {
      console.error(err)
      error(err)
    }
  }
 
  /**
   * Login the user via email magic link.
   * 
   * - send sign-in email
   * - wait for confirmation
   * - authorize
   */

  async login({ email, affiliate }) {
    let token

    ctx.info('sending email', { email, affiliate })
    const { code } = await this.client.Login({ email, affiliate })

    ctx.info('polling for access token', { email })
    while (1) {
      try {
        const res = await this.client.GetAccessToken({ email, code })
        token = res.token
        break
      } catch (err) {
        if (err.type == 'invalid_code') {
          ctx.info('unauthenticated, retrying')
          await sleep(3000)
          continue
        }

        throw err
      }
    }

    ctx.info('received access token')
    this.authorize({ email, token })
  }

  /**
   * Login the user with code.
   */

  async loginWithCode({ email, code, team_id }) {
    try {
      ctx.info('fetching access token', { email, team_id })
      const { token } = await this.client.GetAccessToken({ email, code })

      ctx.info('received access token')
      this.authorize({ email, token })

      if (team_id) {
        ctx.info('setting active team', { team_id })
        localStorage.activeTeamId = team_id
      }

      await this.loadTeams()
    } catch (err) {
      this.error(err)
    }
  }

  /**
   * Login with access token.
   */

  async loginWithToken({ token, team_id  }) {
    try {
      ctx.info('login with token', { team_id })
      const { email } = auth.parse(token)
      this.authorize({ email, token })

      if (team_id) {
        ctx.info('setting active team', { team_id })
        localStorage.activeTeamId = team_id
      }

      await this.loadTeams()
    } catch (err) {
      this.error(err)
    }
  }

  /**
   * Authorize the user:
   * 
   * - set cookie
   * - set `user` state
   * - set client auth token
   */

  authorize({ email, token }) {
    ctx.info('login', { email }) 
    auth.set(token)
    try {
      const user = auth.parse(token)
      this.state.user = user
      this.client.authToken = token
    } catch (err) {
      this.error(err)
    }
    this.render()
  }

  /**
   * Logout the user:
   * 
   * - clear cookie
   * - reset state
   * - reset client auth token
   */

  logout() {
    ctx.info('logout')
    auth.remove()
    delete localStorage.activeTeamId
    this.state = {}
    this.client.authToken = null
    this.render()
  }

  /**
   * Initialize the active team to the first or activeTeamId 
   * stored in localStorage.
   */

  async initializeActiveTeam() {
    const active = localStorage.activeTeamId
    const teams = this.state.teams
    let team = teams[0]

    if (active) {
      ctx.info('found active team', { active })
      const t = teams.find(t => t.id == active)
      if (t) team = t
    }

    if (team) {
      this.changeTeam(team)
    }
  }

  /**
   * Change teams.
   */

  changeTeam(team) {
    // TODO: changeTeam should take an id?
    ctx.info('change team', team)
    localStorage.activeTeamId = team.id
    const { teams, user, products } = this.state
    this.state = { team, teams, user, products }
    this.render()
  }

  /**
   * Load account information.
   */

  async loadAccount() {
    ctx.info('loading account')
    try {
      const { user } = await this.client.GetAccount()
      this.state.user = user
      this.render()
    } catch (err) {
      this.error(err)
    }
  }

  /**
   * Update account information.
   */

  async updateAccount({ name }) {
    ctx.info('updating account', { name })
    try {
      await this.client.UpdateAccount({ name })
      this.state.user.name = name
      this.render()
    } catch (err) {
      this.error(err)
    }
  }

  /**
   * Add a new team.
   */

  async addTeam(team) {
    ctx.info('adding team', team)
    try {
      const res = await this.client.AddTeam(team)
      await this.loadTeams()
      return res
    } catch (err) {
      this.error(err)
    }
  }

  /**
   * Update team information.
   */

  async updateTeam(team) {
    ctx.info('updating team', team)
    try {
      await this.client.UpdateTeam(team)
      await this.loadTeams()
    } catch (err) {
      this.error(err)
    }
  }

  /**
   * Remove a team.
   */

  async removeTeam({ team_id }) {
    ctx.info('removing team', { team_id })
    try {
      await this.client.RemoveTeam({ team_id })
      await this.loadTeams()
    } catch (err) {
      this.error(err)
    }
  }

  /**
   * Load teams the user owns or is a member of.
   */

  async loadTeams() {
    ctx.info('loading teams')
    try {
      const { teams } = await this.client.GetTeams()
      this.state.teams = teams
    } catch (err) {
      this.error(err)
    }
    this.render()
  }

  /**
   * Load team members.
   */

  async loadTeamMembers({ team_id }) {
    ctx.info('loading team members', { team_id })
    try {
      const { members } = await this.client.GetTeamMembers({ team_id })
      this.state.members = members
    } catch (err) {
      this.error(err)
    }
    this.render()
  }

  /**
   * Load team invites.
   */

  async loadTeamInvites({ team_id }) {
    ctx.info('loading team invites', { team_id })
    try {
      const { invites } = await this.client.GetTeamInvites({ team_id })
      this.state.invites = invites
    } catch (err) {
      this.error(err)
    }
    this.render()
  }

  /**
   * Load products.
   */

  async loadProducts() {
    ctx.info('loading products')
    try {
      const { products } = await this.client.GetProducts()
      this.state.products = products
    } catch (err) {
      this.error(err)
    }
    this.render()
  }

  /**
   * Load invites.
   */

  async loadInvoices({ team_id }) {
    ctx.info('loading team invoices', { team_id })
    try {
      const { invoices } = await this.client.GetInvoices({ team_id })
      this.state.invoices = invoices
    } catch (err) {
      this.error(err)
    }
    this.render()
  }

  /**
   * Add a subscription.
   */

  async addSubscription({ team_id, plan_id, coupon }) {
    ctx.info('add subscription', { team_id, plan_id, coupon })
    try {
      await this.client.AddSubscription({ team_id, plan_id, coupon })
      await this.loadSubscriptions({ team_id })
    } catch (err) {
      this.error(err)
    }
    this.render()
  }

  /**
   * Update a subscription.
   */

  async updateSubscription({ team_id, plan_id, subscription_id, coupon }) {
    ctx.info('update subscription', { team_id, plan_id, subscription_id, coupon })
    try {
      await this.client.UpdateSubscription({ team_id, plan_id, subscription_id, coupon })
      await this.loadSubscriptions({ team_id })
    } catch (err) {
      this.error(err)
    }
    this.render()
  }

  /**
   * Remove a subscription.
   */

  async removeSubscription({ team_id, subscription_id }) {
    ctx.info('remove subscription', { team_id, subscription_id })
    try {
      await this.client.RemoveSubscription({ team_id, subscription_id })
      await this.loadSubscriptions({ team_id })
    } catch (err) {
      this.error(err)
    }
    this.render()
  }

  /**
   * Load subscriptions.
   */

  async loadSubscriptions({ team_id }) {
    ctx.info('loading team subscriptions', { team_id })
    try {
      const { subscriptions } = await this.client.GetSubscriptions({ team_id })
      this.state.subscriptions = subscriptions
    } catch (err) {
      this.error(err)
    }
    this.render()
  }

  /**
   * Remove team member.
   */

  async removeTeamMember({ team_id, email }) {
    ctx.info('removing team member or invite', { team_id, email })
    try {
      await this.client.RemoveTeamMember({ team_id, email })

      // TODO: should use hooks on a remove member action?
      if (this.state.user.email == email) {
        ctx.info('removed self from active team')
        const team = this.state.teams.find(t => t.id != team_id)
        if (team) {
          ctx.info('found another team, switching', team)
          this.changeTeam(team)
          team_id = team.id
        }
      }

      await Promise.all([
        this.loadTeamMembers({ team_id }),
        this.loadTeamInvites({ team_id })
      ])
    } catch (err) {
      this.error(err)
    }
  }

  /**
   * Add team member.
   */

  async addTeamMember({ team_id, email, application_name, application_url }) {
    ctx.info('add member', { team_id, email, application_name, application_url })
    try {
      await this.client.AddTeamMember({ team_id, email, application_name, application_url })
      await Promise.all([
        this.loadTeamMembers({ team_id }),
        this.loadTeamInvites({ team_id })
      ])
    } catch (err) {
      this.error(err)
    }
  }

  /**
   * Add Stripe credit card.
   */

  async addCard({ team, token }) {
    ctx.info('add card', { team, token })
    try {
      await this.client.UpdateCreditCard({ team_id: team.id, token })
      await this.loadCard({ team_id: team.id })
    } catch (err) {
      this.error(err)
    }
  }

  /**
   * Load card information.
   */

  async loadCard({ team_id }) {
    ctx.info('load card', { team_id })
    try {
      const { card } = await this.client.GetCreditCard({ team_id })
      this.state.card = { data: card }
      this.render()
    } catch (err) {
      if (err.type == 'not_found') {
        ctx.info('card not found')
        this.state.card = { data: null }
        this.render()
        return
      }
      this.error(err)
    }
  }

  /**
   * Validate a coupon.
   */

  async validateCoupon({ coupon }) {
    ctx.info('validating coupon', { coupon })
    try {
      const res = await this.client.GetCoupon({ coupon })
      return { valid: true, ...res.coupon }
    } catch (err) {
      return { valid: false }
    }
  }
}

